import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { ShoppingCart, ChevronLeft, ChevronRight, X, ZoomIn, Minus, Plus, MessageCircle, Truck, MapPin, ChevronDown, RotateCcw } from 'lucide-react';

const ProductCard = ({ product, onQuickView }) => {
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isZoomOpen, setIsZoomOpen] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(
    product.variants && product.variants.length > 0 ? product.variants[0].name : null
  );

  const isCustom = product.custom_order === true;
  const hasVariants = product.variants && product.variants.length > 0;
  const hasCustomOption = product.has_custom_option === true;

  const activePrice = hasVariants && selectedVariant
    ? (product.variants.find(v => v.name === selectedVariant)?.price || product.price)
    : product.price;

  React.useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && isZoomOpen) setIsZoomOpen(false);
    };
    if (isZoomOpen) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [isZoomOpen]);

  const handleAddToCart = async () => {
    setAdding(true);
    const success = await addToCart(product.id, quantity, selectedVariant);
    if (success) {
      setTimeout(() => { setAdding(false); setQuantity(1); }, 1000);
    } else {
      setAdding(false);
    }
  };

  const images = product.images && product.images.length > 0 ? product.images : [];

  const nextImage = (e) => { e.preventDefault(); e.stopPropagation(); setCurrentImageIndex((prev) => (prev + 1) % images.length); };
  const prevImage = (e) => { e.preventDefault(); e.stopPropagation(); setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length); };
  const handleImageClick = () => { if (images.length > 0) setIsZoomOpen(true); };
  const incrementQuantity = () => { if (quantity < 10) setQuantity(quantity + 1); };
  const decrementQuantity = () => { if (quantity > 1) setQuantity(quantity - 1); };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name.replace(/_/g, ' '),
    "description": product.description,
    "image": images.length > 0 ? images[0] : undefined,
    "brand": { "@type": "Brand", "name": "Rasaa Designs Co" },
    "offers": isCustom ? {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "USD",
      "price": "0",
      "priceValidUntil": new Date(Date.now() + 365 * 86400000).toISOString().split('T')[0],
      "description": "Custom pricing - contact for quote"
    } : {
      "@type": "Offer",
      "priceCurrency": "USD",
      "price": activePrice.toFixed(2),
      "priceValidUntil": new Date(Date.now() + 365 * 86400000).toISOString().split('T')[0],
      "availability": "https://schema.org/InStock",
      "seller": { "@type": "Organization", "name": "Rasaa Designs Co" },
      ...(product.free_shipping ? { "shippingDetails": { "@type": "OfferShippingDetails", "shippingRate": { "@type": "MonetaryAmount", "value": "0", "currency": "USD" } } } : {})
    }
  };

  const jsonLdString = JSON.stringify(jsonLd).replace(/</g, '\\u003c');

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLdString }} />
      <div className="product-card bg-white/40 border border-[#D7C0A0]/50 p-4 group" data-testid={`product-card-${product.id}`}>
        {/* Badges */}
        <div className="flex flex-wrap gap-1.5 mb-2">
          {product.free_shipping && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 rounded-sm" data-testid="free-shipping-badge">
              <Truck className="w-3 h-3" /> Free Shipping (US)
            </span>
          )}
          {product.local_only && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-sm" data-testid="local-only-badge">
              <MapPin className="w-3 h-3" /> Local Pickup / Delivery Only
            </span>
          )}
          {['woodwork_and_art', 'copperware', 'candles'].includes(product.category) && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 rounded-sm" data-testid="return-badge">
              <RotateCcw className="w-3 h-3" /> 30-Day Return
            </span>
          )}
        </div>

        {/* Image Carousel */}
        <div className="relative mb-3 overflow-hidden bg-[#EBE5DA] aspect-square cursor-pointer" onClick={handleImageClick} data-testid="product-image-carousel">
          {images.length > 0 ? (
            <>
              <img
                src={images[currentImageIndex]}
                alt={`${product.name} - Image ${currentImageIndex + 1}`}
                className="w-full h-full object-contain transition-opacity duration-300"
                loading="lazy"
                onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
              />
              <div className="w-full h-full absolute inset-0 hidden items-center justify-center text-[#8D7B68] text-sm">Product Image</div>
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex flex-col items-center justify-center gap-2">
                <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                {onQuickView && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onQuickView(); }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 hover:bg-white text-[#2C1810] text-xs font-medium px-4 py-1.5 rounded-full"
                    data-testid="quick-view-button"
                  >
                    Quick View
                  </button>
                )}
              </div>
              {images.length > 1 && (
                <>
                  <button onClick={prevImage} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" data-testid="prev-image-button">
                    <ChevronLeft className="w-4 h-4 text-[#2C1810]" />
                  </button>
                  <button onClick={nextImage} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" data-testid="next-image-button">
                    <ChevronRight className="w-4 h-4 text-[#2C1810]" />
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                    {images.map((img, index) => (
                      <button key={`dot-${img.slice(-20)}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCurrentImageIndex(index); }}
                        className={`w-2 h-2 rounded-full transition-colors duration-300 ${index === currentImageIndex ? 'bg-[#B87333]' : 'bg-white/60 hover:bg-white/80'}`}
                        data-testid={`image-dot-${index}`} />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#8D7B68] text-sm">Product Image</div>
          )}
        </div>

        {/* Thumbnails */}
        {images.length > 1 && (
          <div className="flex gap-2 mb-4 overflow-x-auto pb-2" data-testid="image-thumbnails">
            {images.slice(0, 6).map((image, index) => (
              <button key={`thumb-${image.slice(-20)}`} onClick={() => setCurrentImageIndex(index)}
                className={`flex-shrink-0 w-16 h-16 rounded-sm overflow-hidden border-2 transition-all duration-300 ${index === currentImageIndex ? 'border-[#B87333] scale-105' : 'border-[#D7C0A0]/50 hover:border-[#B87333]/50'}`}
                data-testid={`thumbnail-${index}`}>
                <img src={image} alt={`${product.name} thumbnail ${index + 1}`} className="w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
            {images.length > 6 && (
              <div className="flex-shrink-0 w-16 h-16 rounded-sm border-2 border-[#D7C0A0]/50 flex items-center justify-center text-xs text-[#8D7B68]">
                +{images.length - 6}
              </div>
            )}
          </div>
        )}

        {/* Product Info */}
        <div className="space-y-2">
          <h3 className="text-xl font-medium" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="product-name">
            {product.name.replace(/_/g, ' ')}
          </h3>
          {product.subtitle && (
            <p className="text-xs font-medium text-[#B87333] tracking-wide" data-testid="product-subtitle">
              {product.subtitle}
            </p>
          )}
          <p className="text-sm text-[#5D4037] leading-relaxed" data-testid="product-description">
            {product.description}
          </p>

          <div className="pt-3">
            {/* Price */}
            {!isCustom && (
              <p className="text-xl font-semibold text-[#2C1810] mb-3" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="product-price">
                ${activePrice.toFixed(2)}
              </p>
            )}
            {isCustom ? (
              /* Custom Order CTA */
              <div className="space-y-3">
                <p className="text-lg font-medium text-[#B87333]" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="custom-pricing-label">
                  Custom Pricing
                </p>
                <p className="text-xs text-[#8D7B68]">
                  Every custom product is unique. Contact us to discuss your vision and receive a personalized quote.
                </p>
                <button onClick={() => navigate('/contact')}
                  className="w-full bg-[#2C1810] text-white px-3 py-3 rounded-sm hover:bg-[#3D2920] transition-colors duration-300 flex items-center justify-center gap-2 text-sm md:text-base"
                  data-testid="contact-for-custom-button">
                  <MessageCircle className="w-4 h-4 shrink-0" />
                  <span>Contact for Custom Design</span>
                </button>
              </div>
            ) : (
              <>
                {/* Variant Selector */}
                {hasVariants && (
                  <div className="mb-3" data-testid="variant-selector">
                    <label className="block text-xs font-medium text-[#5D4037] mb-1">Select Option:</label>
                    <div className="relative">
                      <select
                        value={selectedVariant || ''}
                        onChange={(e) => setSelectedVariant(e.target.value)}
                        className="w-full appearance-none bg-white/60 border border-[#D7C0A0] px-3 py-2 pr-8 text-sm focus:outline-none focus:border-[#B87333] transition-colors rounded-sm"
                        data-testid="variant-dropdown"
                      >
                        {product.variants.map((v) => (
                          <option key={v.name} value={v.name}>
                            {v.name} — ${v.price.toFixed(2)}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8D7B68] pointer-events-none" />
                    </div>
                  </div>
                )}

                {/* Quantity + Explore */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  <div className="flex items-center border border-[#D7C0A0] rounded-sm" data-testid="quantity-selector">
                    <button onClick={decrementQuantity} className="p-2 hover:bg-[#EBE5DA] transition-colors" data-testid="quantity-decrease">
                      <Minus className="w-4 h-4 text-[#2C1810]" />
                    </button>
                    <input type="number" min="1" max="10" value={quantity}
                      onChange={(e) => { const val = parseInt(e.target.value) || 1; setQuantity(Math.min(10, Math.max(1, val))); }}
                      className="w-14 text-center border-x border-[#D7C0A0] focus:outline-none bg-transparent"
                      data-testid="quantity-input" />
                    <button onClick={incrementQuantity} className="p-2 hover:bg-[#EBE5DA] transition-colors" data-testid="quantity-increase">
                      <Plus className="w-4 h-4 text-[#2C1810]" />
                    </button>
                  </div>
                  <button onClick={handleAddToCart} disabled={adding}
                    className="flex-1 bg-[#B87333] text-white px-3 py-2 rounded-sm hover:bg-[#A05A20] transition-colors duration-300 flex items-center justify-center gap-2 text-sm md:text-base whitespace-nowrap"
                    data-testid="add-to-cart-button">
                    <ShoppingCart className="w-4 h-4 shrink-0" />
                    <span className="truncate">{adding ? 'Added!' : 'Add to Cart'}</span>
                  </button>
                </div>

                {/* Custom Option Link */}
                {hasCustomOption && (
                  <button onClick={() => navigate('/contact')}
                    className="w-full mt-2 border border-[#2C1810] text-[#2C1810] px-3 py-2 rounded-sm hover:bg-[#2C1810] hover:text-white transition-colors duration-300 flex items-center justify-center gap-2 text-sm"
                    data-testid="custom-option-button">
                    <MessageCircle className="w-4 h-4 shrink-0" />
                    <span>Custom orders available — Contact Us</span>
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Zoom Modal - Scrollable */}
      {isZoomOpen && images.length > 0 && (
        <div className="fixed inset-0 bg-black/90 z-50 flex flex-col" onClick={() => setIsZoomOpen(false)} data-testid="image-zoom-modal">
          {/* Fixed top bar */}
          <div className="flex-shrink-0 flex items-center justify-between px-6 py-3 relative z-10" onClick={(e) => e.stopPropagation()}>
            <div className="bg-black/50 px-4 py-1.5 rounded-full">
              <p className="text-white text-sm">{currentImageIndex + 1} / {images.length}</p>
            </div>
            <button onClick={() => setIsZoomOpen(false)} className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors" data-testid="close-zoom-button">
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
          {/* Scrollable image area */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-center px-4 pb-20">
              <img src={images[currentImageIndex]} alt={`${product.name} - Zoomed view`} className="max-w-full w-auto h-auto" style={{ maxWidth: '900px' }} data-testid="zoomed-image" />
            </div>
          </div>
          {/* Fixed navigation arrows */}
          {images.length > 1 && (
            <>
              <button onClick={prevImage} className="fixed left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 p-3 rounded-full transition-colors z-20" data-testid="zoom-prev-button">
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              <button onClick={nextImage} className="fixed right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 p-3 rounded-full transition-colors z-20" data-testid="zoom-next-button">
                <ChevronRight className="w-6 h-6 text-white" />
              </button>
              <div className="fixed bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20" onClick={(e) => e.stopPropagation()}>
                {images.map((img, index) => (
                  <button key={`zoom-${img.slice(-20)}`} onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(index); }}
                    className={`w-3 h-3 rounded-full transition-colors ${index === currentImageIndex ? 'bg-[#B87333]' : 'bg-white/60 hover:bg-white/80'}`}
                    data-testid={`zoom-dot-${index}`} />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default ProductCard;
